import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';

interface College {
  id: number;
  name: string;
}

interface OptionForm {
  text: string;
  college_ids: number[];
  order_no: number;
}

interface QuestionForm {
  id?: number;
  text: string;
  order_no: number;
  options: OptionForm[];
}

interface Question {
  id: number;
  text: string;
  order_no: number;
  options: { id: number; text: string; college_ids: number[]; order_no: number }[];
}

const emptyQuestion = (order_no: number): QuestionForm => ({
  text: '',
  order_no,
  options: [],
});

export default function Questions() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [colleges, setColleges] = useState<College[]>([]);
  const [editing, setEditing] = useState<QuestionForm | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = () => {
    Promise.all([
      api.get('/api/questions'),
      api.get('/api/colleges'),
    ]).then(([qRes, cRes]) => {
      setQuestions(qRes.data);
      setColleges(cRes.data);
      setLoading(false);
    }).catch(() => navigate('/admin/login'));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.text) { alert('请填写题目内容'); return; }
    if (editing.options.length < 2) { alert('至少需要2个选项'); return; }
    for (const opt of editing.options) {
      if (!opt.text) { alert('选项内容不能为空'); return; }
      if (!opt.college_ids || opt.college_ids.length === 0) {
        alert('每个选项至少绑定一个书院'); return;
      }
    }
    try {
      if (editing.id) {
        await api.put(`/api/admin/questions/${editing.id}`, editing);
      } else {
        await api.post('/api/admin/questions', editing);
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      alert('保存失败，请检查是否已登录');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确认删除该题目？')) return;
    await api.delete(`/api/admin/questions/${id}`);
    fetchAll();
  };

  const addOption = () => {
    if (!editing) return;
    setEditing({
      ...editing,
      options: [
        ...editing.options,
        { text: '', college_ids: [], order_no: editing.options.length + 1 }
      ]
    });
  };

  const removeOption = (idx: number) => {
    if (!editing) return;
    const opts = editing.options.filter((_, i) => i !== idx)
      .map((o, i) => ({ ...o, order_no: i + 1 }));
    setEditing({ ...editing, options: opts });
  };

  const updateOption = (idx: number, field: keyof OptionForm, value: any) => {
    if (!editing) return;
    const opts = editing.options.map((o, i) =>
      i === idx ? { ...o, [field]: value } : o
    );
    setEditing({ ...editing, options: opts });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">题目管理</h1>
          <div className="flex gap-3">
            <button onClick={() => navigate('/admin/dashboard')}
              className="text-sm text-gray-500 hover:text-gray-700">← 返回</button>
            <button
              onClick={() => setEditing(emptyQuestion(questions.length + 1))}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700"
            >
              + 新增题目
            </button>
          </div>
        </div>

        {loading ? <p className="text-gray-400">加载中...</p> : (
          <div className="space-y-4">
            {questions.length === 0 && (
              <div className="bg-white rounded-xl p-8 text-center text-gray-400">
                暂无题目，点击右上角新增
              </div>
            )}
            {questions.map((q, idx) => (
              <div key={q.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <p className="font-medium text-gray-800">{q.text}</p>
                    </div>
                    <div className="ml-9 space-y-1">
                      {q.options.map((opt, oidx) => (
                        <div key={opt.id} className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                          <span className="text-gray-400">{oidx + 1}.</span>
                          <span>{opt.text}</span>
                          {(opt.college_ids || []).map((cid: number) => {
                            const college = colleges.find(c => c.id === cid);
                            return (
                              <span key={cid} className="bg-indigo-50 text-indigo-600 text-xs px-2 py-0.5 rounded-full">
                                {college?.name || '未知书院'}
                              </span>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button onClick={() => setEditing({
                      id: q.id, text: q.text, order_no: q.order_no,
                      options: q.options.map(o => ({
                        text: o.text, college_ids: o.college_ids || [], order_no: o.order_no
                      }))
                    })} className="text-sm text-indigo-600 hover:text-indigo-800">编辑</button>
                    <button onClick={() => handleDelete(q.id)}
                      className="text-sm text-red-500 hover:text-red-700">删除</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg my-4">
              <h2 className="text-lg font-bold mb-4">{editing.id ? '编辑题目' : '新增题目'}</h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">题目内容</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                  rows={3}
                  placeholder="请输入题目描述"
                  value={editing.text}
                  onChange={e => setEditing({ ...editing, text: e.target.value })}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">题目序号</label>
                <input
                  type="number"
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  value={editing.order_no}
                  onChange={e => setEditing({ ...editing, order_no: parseInt(e.target.value) || 1 })}
                />
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    选项（{editing.options.length} 个）
                  </label>
                  <button onClick={addOption}
                    className="text-sm text-indigo-600 hover:text-indigo-800">
                    + 添加选项
                  </button>
                </div>
                <div className="space-y-3">
                  {editing.options.map((opt, idx) => (
                    <div key={idx} className="flex gap-2 items-start bg-gray-50 rounded-lg p-3">
                      <span className="text-sm text-gray-400 mt-2">{idx + 1}.</span>
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="选项描述"
                          value={opt.text}
                          onChange={e => updateOption(idx, 'text', e.target.value)}
                        />
                        <div className="flex flex-wrap gap-2">
                          {colleges.map(c => (
                            <label key={c.id} className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={opt.college_ids?.includes(c.id) || false}
                                onChange={e => {
                                  const current = opt.college_ids || [];
                                  const updated = e.target.checked
                                    ? [...current, c.id]
                                    : current.filter((id: number) => id !== c.id);
                                  updateOption(idx, 'college_ids', updated);
                                }}
                              />
                              <span className="text-sm text-gray-700">{c.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => removeOption(idx)}
                        className="text-red-400 hover:text-red-600 mt-1.5">✕</button>
                    </div>
                  ))}
                  {editing.options.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">
                      点击"添加选项"开始添加
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setEditing(null)}
                  className="flex-1 border border-gray-300 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                  取消
                </button>
                <button onClick={handleSave}
                  className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700">
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}